import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaChalkboardTeacher,
  FaGlobeEurope,
  FaClock,
  FaHeadset,
  FaPenFancy,
  FaVideo,
  FaBookOpen,
} from "react-icons/fa";
import { GiTeacher } from "react-icons/gi";
import Flag from "react-world-flags";
import AOS from "aos";
import "aos/dist/aos.css";

const metricsConfig = [
  {
    label: "Students Trained",
    value: 4500,
    suffix: "+",
    sub: "From beginners to advanced speakers",
  },
  {
    label: "Certified Trainers",
    value: 85,
    suffix: "",
    sub: "Language trainers & industry mentors",
  },
  {
    label: "Alumni Worldwide",
    value: 22,
    suffix: "+",
    sub: "Countries where our learners are based",
  },
];

const featureCards = [
  {
    icon: <FaChalkboardTeacher />,
    title: "Experienced Faculty",
    text: "Learn directly from certified trainers with years of classroom and corporate experience.",
  },
  {
    icon: <FaGlobeEurope />,
    title: "International Curriculum",
    text: "Stay aligned with DELF, Goethe, JLPT, DELE and global exam standards from day one.",
  },
  {
    icon: <FaClock />,
    title: "Flexible Batches",
    text: "Weekend, weekday, and fast-track options designed to fit around your work or studies.",
  },
  {
    icon: <FaHeadset />,
    title: "Chat Support 24/7",
    text: "Get doubts clarified on the go with responsive support between classes.",
  },
  {
    icon: <FaPenFancy />,
    title: "Regular Mock Tests",
    text: "Stay exam-ready with structured mock tests and personalised score analysis.",
  },
  {
    icon: <FaVideo />,
    title: "Session Recordings",
    text: "Revisit tricky topics anytime with access to full lesson recordings.",
  },
  {
    icon: <FaBookOpen />,
    title: "Online Study Materials",
    text: "Handpicked notes, quizzes, and assignments to keep your learning on track.",
  },
  {
    icon: <GiTeacher />,
    title: "Free Trial Sessions",
    text: "Experience our teaching style and class environment before you commit.",
  },
];

const courses = [
  {
    name: "French",
    description:
      "Master DELF A1–B2 with structured speaking, writing, and listening modules.",
    path: "/learn-language#french",
    badge: "Most Popular",
  },
  {
    name: "German",
    description:
      "Crack Goethe levels with focused grammar and real-world business vocabulary.",
    path: "/german",
  },
  {
    name: "Spanish",
    description:
      "Build fluency and confidence to connect across Latin America & Europe.",
    path: "/spanish",
  },
  {
    name: "Japanese",
    description:
      "JLPT-aligned prep with kanji drills, listening labs, and culture insights.",
    path: "/japanese",
  },
  {
    name: "French",
    description:
      "Learn the language of art, diplomacy, and global hospitality careers.",
    path: "/french",
  },
  {
    name: "Junior Master",
    description:
      "Fun, interactive foreign language foundation for kids aged 7–14.",
    path: "/learn-language#junior-master",
  },
];

const testimonials = [
  {
    name: "Anjali Verma",
    course: "B2 French Certification",
    quote:
      "The trainers at Lingua Alliance truly care about your progress. Their feedback and immersive sessions helped me secure a position with a global NGO.",
    avatar:
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=facearea&w=150&h=150&q=80",
  },
  {
    name: "Rahul Mehta",
    course: "Business German Fast Track",
    quote:
      "Practical role-plays and real-world vocabulary made all the difference. I closed my first German client within two months of completing the course.",
    avatar:
      "https://images.unsplash.com/photo-1619895862022-09114b41f16f?auto=format&fit=facearea&w=150&h=150&q=80",
  },
  {
    name: "Sakura Tan",
    course: "JLPT N3 Prep",
    quote:
      "I loved the mock tests and doubt-solving clinic. The mentors ensured every grammar concept was crystal clear before the exam.",
    avatar:
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=facearea&w=150&h=150&q=80",
  },
  {
    name: "Carlos Rivera",
    course: "Spanish for Global Sales",
    quote:
      "Interactive role-plays and cultural immersion projects helped me close partnerships across Latin America with confidence and empathy.",
    avatar:
      "https://images.unsplash.com/photo-1527980965255-d3b416303d12?auto=format&fit=facearea&w=150&h=150&q=80",
  },
  {
    name: "Emily Chen",
    course: "Corporate Mandarin Intensive",
    quote:
      "From vocabulary drills to real business scenarios, the mentors made every session practical. I led my first Mandarin presentation after eight weeks.",
    avatar:
      "https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=facearea&w=150&h=150&q=80",
  },
];

const blogPosts = [
  {
    title: "5 Secrets to Mastering a New Language Faster",
    excerpt:
      "Discover practical hacks our top performers use to stay ahead of the curve and speak confidently in record time.",
    image:
      "https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?auto=format&fit=crop&w=800&q=80",
  },
  {
    title: "Career Roadmap: Languages for Global Professionals",
    excerpt:
      "From Europe to Asia—find out which language opens doors to your dream career path and how to get started today.",
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800&q=80",
  },
  {
    title: "How Kids Thrive in Multilingual Classrooms",
    excerpt:
      "Explore the cognitive advantages and fun activities that turn language learning into a superpower for children.",
    image:
      "https://images.unsplash.com/photo-1529070538774-1843cb3265df?auto=format&fit=crop&w=800&q=80",
  },
];

const brandBadges = [
  { label: "DELF", description: "Alliance Française Certified" },
  { label: "Goethe", description: "German Language Credential" },
  { label: "JLPT", description: "Japanese Proficiency Levels" },
  { label: "DELE", description: "Instituto Cervantes Certified" },
];

const sliderImages = [
  `${process.env.PUBLIC_URL}/Header_Background.png`,
  `${process.env.PUBLIC_URL}/Header_Background1.png`,
  `${process.env.PUBLIC_URL}/Header_Background2.png`,
];

const Home = () => {
  const [animatedMetrics, setAnimatedMetrics] = useState(
    metricsConfig.map(() => 0)
  );
  const [displayIndex, setDisplayIndex] = useState(0);
  const [highlightedCourse, setHighlightedCourse] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const trackRef = useRef(null);
  const [cardOffset, setCardOffset] = useState(0);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const metricsRef = useRef(null);
  const [metricsVisible, setMetricsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const metricSpeeds = useMemo(
    () => metricsConfig.map((metric) => Math.ceil(metric.value / 40)),
    []
  );

  const cloneCount = useMemo(
    () => Math.min(visibleCards, testimonials.length || 0),
    [visibleCards]
  );

  const extendedTestimonials = useMemo(() => {
    if (!testimonials.length) {
      return [];
    }
    const head = testimonials.slice(0, cloneCount);
    const tail = testimonials.slice(-cloneCount);
    return [...tail, ...testimonials, ...head];
  }, [cloneCount]);

  const totalActual = testimonials.length;

  const activeIndex = totalActual
    ? (((displayIndex - cloneCount) % totalActual) + totalActual) % totalActual
    : 0;

  const updateVisibleCards = useCallback(() => {
    if (window.innerWidth <= 640) {
      setVisibleCards(1);
    } else if (window.innerWidth <= 1024) {
      setVisibleCards(2);
    } else {
      setVisibleCards(3);
    }
  }, []);

  const updateCardOffset = useCallback(() => {
    if (!trackRef.current) return;

    const cards = trackRef.current.querySelectorAll(".testimonial-card");
    if (!cards.length) return;

    if (cards.length > 1) {
      const firstRect = cards[0].getBoundingClientRect();
      const secondRect = cards[1].getBoundingClientRect();
      const offset = Math.abs(secondRect.left - firstRect.left);
      if (offset > 0) {
        setCardOffset(offset);
        return;
      }
    }
    const width = cards[0].getBoundingClientRect().width;
    setCardOffset(width);
  }, []);

  useEffect(() => {
    updateVisibleCards();
    window.addEventListener("resize", updateVisibleCards);
    return () => window.removeEventListener("resize", updateVisibleCards);
  }, [updateVisibleCards]);

  useEffect(() => {
    const measure = () => updateCardOffset();
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [updateCardOffset, visibleCards, extendedTestimonials.length]);

  useEffect(() => {
    if (totalActual) {
      setDisplayIndex(cloneCount);
    } else {
      setDisplayIndex(0);
    }
  }, [cloneCount, totalActual]);

  useEffect(() => {
    if (!totalActual) return;
    const interval = setInterval(() => {
      setDisplayIndex((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [totalActual]);

  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightedCourse((prev) => (prev + 1) % courses.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Intersection Observer for metrics section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setMetricsVisible(true);
            setHasAnimated(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (metricsRef.current) {
      observer.observe(metricsRef.current);
    }

    return () => {
      if (metricsRef.current) {
        observer.unobserve(metricsRef.current);
      }
    };
  }, [hasAnimated]);

  // Animate metrics counter when section is visible
  useEffect(() => {
    if (!metricsVisible) return;

    const interval = setInterval(() => {
      setAnimatedMetrics((prev) =>
        prev.map((value, index) => {
          const nextValue = value + metricSpeeds[index];
          return nextValue >= metricsConfig[index].value
            ? metricsConfig[index].value
            : nextValue;
        })
      );
    }, 50);
    return () => clearInterval(interval);
  }, [metricsVisible, metricSpeeds]);

  useEffect(() => {
    // Preload all slider images
    let loadedCount = 0;
    const totalImages = sliderImages.length;

    sliderImages.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages || loadedCount === 1) {
          setHeroImageLoaded(true);
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages || loadedCount === 1) {
          setHeroImageLoaded(true);
        }
      };
      img.src = src;
    });
  }, []);

  // Slider auto-advance every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlideIndex(
        (prevIndex) => (prevIndex + 1) % sliderImages.length
      );
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );

    const targets = document.querySelectorAll(".reveal-on-scroll");
    targets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isTransitioning) {
      let frame1;
      let frame2;
      frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
      return () => {
        if (frame1) cancelAnimationFrame(frame1);
        if (frame2) cancelAnimationFrame(frame2);
      };
    }
    return undefined;
  }, [isTransitioning]);

  const handleTrackTransitionEnd = useCallback(() => {
    if (!totalActual) return;

    if (displayIndex >= totalActual + cloneCount) {
      setIsTransitioning(false);
      setDisplayIndex(cloneCount);
    } else if (displayIndex < cloneCount) {
      setIsTransitioning(false);
      setDisplayIndex(totalActual + cloneCount - 1);
    }
  }, [displayIndex, cloneCount, totalActual]);

  const handleDotClick = useCallback(
    (index) => {
      if (!totalActual) return;
      setIsTransitioning(true);
      setDisplayIndex(cloneCount + index);
    },
    [cloneCount, totalActual]
  );

  const handleCardClick = useCallback(
    (index) => {
      if (!totalActual) return;
      const normalized =
        (((index - cloneCount) % totalActual) + totalActual) % totalActual;
      setIsTransitioning(true);
      setDisplayIndex(cloneCount + normalized);
    },
    [cloneCount, totalActual]
  );

  const trackIndex = displayIndex;

  return (
    <div className="bg-canvas text-ink relative">
      {/* subtle pattern */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.07]">
        <div className="h-full w-full bg-[radial-gradient(circle,_rgba(255,255,255,0.4)_1px,_transparent_0)] bg-[length:22px_22px]" />
      </div>

      {/* HERO */}
      <section className="relative w-full h-[80vh] min-h-[500px] overflow-hidden flex items-center z-10">
        {/* Slider Images */}
        {sliderImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              currentSlideIndex === index ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
            style={{
              backgroundImage: `url('${image}')`,
            }}
            aria-hidden={currentSlideIndex !== index}
          />
        ))}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1226]/90 via-[#0f1226]/75 to-transparent lg:from-[#0f1226]/95 lg:via-[#0f1226]/75 z-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1226]/45 via-transparent to-transparent z-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0f1226]/30 z-20" />

        <div className="absolute inset-0 overflow-hidden z-10 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-primary/15 blur-2xl animate-pulse pointer-events-none"
              style={{
                width: `${90 + i * 40}px`,
                height: `${90 + i * 40}px`,
                left: `${8 + i * 14}%`,
                top: `${18 + i * 9}%`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${3.5 + i * 0.4}s`,
              }}
            />
          ))}
        </div>

        <div className="relative z-30 w-full px-6 py-12 lg:pl-16 lg:pr-6 xl:pl-24">
          <div className="space-y-6 max-w-2xl">
            <div
              data-aos="fade-down"
              data-aos-delay="100"
              data-aos-duration="800"
              className="flex items-center gap-3 flex-wrap"
            >
              <span className="inline-flex w-fit items-center rounded-full bg-primary/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-md border border-primary/60 shadow-[0_4px_12px_rgba(79,64,198,0.3)]">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                Learn French · German · Spanish · Japanese
              </span>
              {/* Small Flags */}
              <div className="flex items-center gap-2">
                {[
                  { flagCode: "FR" },
                  { flagCode: "DE" },
                  { flagCode: "ES" },
                  { flagCode: "JP" },
                ].map((lang) => (
                  <div
                    key={lang.flagCode}
                    className="flex h-6 w-8 items-center justify-center overflow-hidden rounded border border-white/40 bg-white/20 backdrop-blur-sm shadow-sm"
                  >
                    <Flag
                      code={lang.flagCode}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="200"
              data-aos-duration="1000"
              className="space-y-4"
            >
              <h1 className="text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl lg:leading-[1.15] drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)] font-display">
                <span className="text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  Online Language Courses
                </span>
                <br />
                <span className="bg-gradient-to-r from-primary-soft via-primary to-primary-soft bg-clip-text text-transparent animate-gradient drop-shadow-[0_2px_8px_rgba(79,64,198,0.4)]">
                  for a Global Career
                </span>
              </h1>
              <p className="text-base leading-relaxed text-white sm:text-lg drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)] font-medium">
                Structured A1–C2 programs, live speaking practice, and
                exam-aligned content to help you communicate confidently in
                French, German, Spanish, and Japanese.
              </p>
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="300"
              data-aos-duration="1000"
              className="flex flex-wrap items-center gap-4"
            >
              <a
                className="relative inline-flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-primary via-primary-soft to-primary px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.45)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(79,64,198,0.55)] hover:scale-105 group"
                href="/learn-language"
              >
                <span className="relative z-10">Explore Language Courses</span>
                <span className="pointer-events-none absolute inset-0 rounded-full bg-white/40 opacity-0 mix-blend-overlay animate-button-shine group-hover:opacity-100" />
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </a>
              <a
                className="inline-flex items-center justify-center rounded-full border-2 border-white/70 bg-white/10 backdrop-blur-md px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition-all duration-300 hover:-translate-y-1 hover:bg-white/20 hover:border-white hover:scale-105 hover:shadow-lg"
                href="/contact"
              >
                Book a Free Demo
              </a>
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="400"
              data-aos-duration="1000"
              className="flex flex-wrap items-center gap-4 pt-2"
            >
              {[
                "Exam-focused batches (DELF, Goethe, JLPT, DELE)",
                "Small groups with live speaking practice",
              ].map((item, index) => (
                <div
                  key={item}
                  data-aos="fade-left"
                  data-aos-delay={900 + index * 100}
                  data-aos-duration="600"
                  className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-md border border-white/40 px-4 py-2 text-xs font-medium text-white hover:bg-white/30 hover:border-white/60 hover:scale-105 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.2)] drop-shadow-[0_1px_3px_rgba(0,0,0,0.3)]"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 hidden lg:flex items-center gap-3 text-xs font-medium text-white/70">
            <span className="h-9 w-[1px] bg-gradient-to-b from-white/70 to-transparent rounded-full animate-pulse" />
            <span>Scroll to explore programs, success stories & resources</span>
          </div>
        </div>

        {/* Slider Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentSlideIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                currentSlideIndex === index
                  ? "w-8 bg-white"
                  : "w-2 bg-white/50 hover:bg-white/75"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* METRICS */}
      <section
        ref={metricsRef}
        className="w-full relative z-10 py-12"
      >
        <div className="pointer-events-none absolute left-1/2 top-[32rem] h-40 w-40 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl sm:left-24 sm:translate-x-0" />
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="flex flex-col gap-3 pb-4 text-center sm:text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0f1226]">
              Trusted by learners around the world
            </p>
          </div>
        </div>
        <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 sm:grid-cols-3 lg:px-6">
          {metricsConfig.map((metric, index) => (
            <div
              key={metric.label}
              data-aos="fade-up"
              data-aos-delay={100 + index * 100}
              data-aos-duration="800"
              className="group relative overflow-hidden rounded-2xl border border-primary/20 bg-white/5 backdrop-blur-sm p-6 shadow-elevated transition hover:-translate-y-1 hover:shadow-float hover:bg-white/10"
            >
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#0f1226] via-[#1a1f3a] to-[#0f1226] transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative space-y-1 ">
                <span className="block text-3xl font-bold text-primary drop-shadow-sm">
                  {animatedMetrics[index]}
                  {metric.suffix}
                </span>
                <span className="text-sm font-semibold text-white">
                  {metric.label}{" "}
                </span>
                <span className="text-xs text-white/70">{metric.sub}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-6">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary"
          >
            Why Choose Us
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-4 text-3xl font-semibold text-ink sm:text-4xl"
          >
            Experience a Dynamic, Learner-First Ecosystem
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-4 text-sm leading-relaxed text-ink-muted sm:text-base"
          >
            Our programs blend live interaction, smart analytics, and
            exam-aligned practice to ensure you stay motivated and achieve your
            language goals faster.
          </p>
        </div>
        <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-6 px-4 sm:grid-cols-2 lg:grid-cols-4 lg:px-6">
          {featureCards.map((card, index) => (
            <article
              key={card.title}
              data-aos="zoom-in"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className="group relative overflow-hidden rounded-2xl border border-primary/10 bg-gradient-to-br from-white via-white to-primary/10 p-6 shadow-elevated transition hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)]"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-xl text-primary animate-icon-bounce relative z-10">
                {card.icon}
              </span>
              <h3 className="mt-4 text-lg font-semibold text-ink relative z-10">
                {card.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-muted relative z-10">
                {card.text}
              </p>
              <div className="pointer-events-none absolute inset-0 translate-y-6 bg-gradient-to-br from-primary/15 to-primary-soft/12 opacity-0 transition group-hover:translate-y-0 group-hover:opacity-100" />
            </article>
          ))}
        </div>
      </section>

      {/* COURSES */}
      <section className="bg-white py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-6">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary"
          >
            Our Courses
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-4 text-3xl font-semibold sm:text-4xl"
          >
            Highlight Languages Tailored to Your Ambition
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-4 text-sm leading-relaxed text-ink-muted sm:text-base"
          >
            Dive into curated roadmaps for exams like DELF, Goethe, HSK, JLPT,
            and more. Choose a track and we will personalize a path that fits
            your schedule.
          </p>
        </div>

        <div className="mx-auto mt-12 flex w-full max-w-6xl flex-wrap justify-center gap-6 px-4 lg:px-6">
          {courses.map((course, index) => (
            <article
              key={`${course.name}-${index}`}
              data-aos="zoom-in"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className={`group relative w-full overflow-hidden rounded-2xl border border-transparent bg-gradient-to-br from-white via-white to-primary/5 p-6 shadow-elevated transition hover:-translate-y-2 hover:border-primary/30 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)] ${
                highlightedCourse === index
                  ? "border-primary/30 shadow-[0_32px_60px_rgba(79,64,198,0.25)]"
                  : ""
              }`}
            >
              <span className="pointer-events-none absolute -right-12 -top-12 h-24 w-24 rounded-full bg-primary/10 blur-2xl opacity-0 transition duration-300 group-hover:opacity-100" />
              {course.badge && (
                <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">
                  {course.badge}
                </span>
              )}
              <h3 className="mt-2 text-xl font-semibold text-ink">
                {course.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                {course.description}
              </p>
              <Link
                className="mt-6 inline-flex items-center text-sm font-semibold text-primary transition hover:text-primary/80"
                to={course.path}
              >
                Learn More →
              </Link>
              {highlightedCourse === index && (
                <span className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-primary/20 animate-highlight-pulse" />
              )}
            </article>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-canvas py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-6">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary"
          >
            Success Stories
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-4 text-3xl font-semibold sm:text-4xl"
          >
            See How Learners Reach Their Goals
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-4 text-sm leading-relaxed text-ink-muted sm:text-base"
          >
            From top corporates to aspiring global citizens, our alumni share
            how language mastery transformed their journeys.
          </p>
        </div>

        <div className="relative mt-12 overflow-hidden px-4 sm:px-6">
          <div
            ref={trackRef}
            className="flex gap-6 sm:gap-8 transition-transform duration-[700ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] pb-4"
            style={{
              transform:
                cardOffset > 0
                  ? `translateX(-${cardOffset * trackIndex}px)`
                  : undefined,
              transition: isTransitioning ? undefined : "none",
            }}
            onTransitionEnd={handleTrackTransitionEnd}
          >
            {extendedTestimonials.map((item, index) => {
              const normalized = totalActual
                ? (((index - cloneCount) % totalActual) + totalActual) %
                  totalActual
                : 0;
              const isActive = normalized === activeIndex;
              return (
                <article
                  key={`${item.name}-${index}`}
                  className={`testimonial-card flex w-full flex-shrink-0 flex-col rounded-2xl border border-primary/10 bg-white p-8 text-center shadow-elevated transition hover:-translate-y-1 hover:shadow-float sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] ${
                    isActive
                      ? "border-primary/30 shadow-[0_24px_48px_rgba(79,64,198,0.25)]"
                      : "opacity-80"
                  }`}
                  style={{ animationDelay: `${normalized * 0.12}s` }}
                  onClick={() => handleCardClick(index)}
                >
                  <div className="relative mx-auto">
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-primary/15 blur-xl" />
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="relative mx-auto h-20 w-20 rounded-full border-4 border-primary/20 object-cover"
                    />
                  </div>
                  <blockquote className="mt-4 text-sm italic leading-relaxed text-ink-muted">
                    “{item.quote}”
                  </blockquote>
                  <h4 className="mt-4 text-lg font-semibold text-ink">
                    {item.name}
                  </h4>
                  <span className="text-sm font-medium text-primary">
                    {item.course}
                  </span>
                </article>
              );
            })}
          </div>
        </div>
        <div className="mt-6 flex justify-center gap-3">
          {testimonials.map((_, index) => (
            <button
              type="button"
              key={index}
              className={`h-2.5 w-2.5 rounded-full transition ${
                activeIndex === index
                  ? "bg-primary"
                  : "bg-primary/25 hover:bg-primary/60"
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Show testimonial ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* BRAND BADGES */}
      <section className="py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-6">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary"
          >
            Brand Partners & Certifications
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-4 text-3xl font-semibold sm:text-4xl"
          >
            We Prepare You for Recognized International Certifications
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-4 text-sm leading-relaxed text-ink-muted sm:text-base"
          >
            Get exam-ready with a curriculum mapped closely to the world’s most
            trusted language certifications and frameworks.
          </p>
        </div>
        <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-6 px-4 sm:grid-cols-2 lg:grid-cols-4 lg:px-6">
          {brandBadges.map((badge, index) => (
            <article
              key={badge.label}
              data-aos="zoom-in"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className="rounded-2xl border border-dashed border-primary/20 bg-gradient-to-br from-white via-white to-primary/10 p-6 text-center transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_26px_52px_rgba(18,10,56,0.18)]"
            >
              <div className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-sm font-semibold text-primary">
                {badge.label}
              </div>
              <p className="mt-3 text-sm text-ink-muted">{badge.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* BLOGS */}
      <section className="bg-white py-20 relative z-10">
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-6">
          <span
            data-aos="fade-down"
            data-aos-duration="800"
            className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary"
          >
            Latest Blogs
          </span>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="1000"
            className="mt-4 text-3xl font-semibold sm:text-4xl"
          >
            Insights to Build Momentum
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="mt-4 text-sm leading-relaxed text-ink-muted sm:text-base"
          >
            Stay inspired with curated reads covering study hacks, certification
            tips, and motivational stories from our community.
          </p>
        </div>
        <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-6 px-4 md:grid-cols-2 xl:grid-cols-3 lg:px-6">
          {blogPosts.map((blog, index) => (
            <article
              key={blog.title}
              data-aos="fade-up"
              data-aos-delay={300 + index * 100}
              data-aos-duration="800"
              className="overflow-hidden rounded-2xl bg-white shadow-elevated transition hover:-translate-y-1 hover:shadow-float"
            >
              <div className="h-48 w-full overflow-hidden">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="h-full w-full object-cover transition duration-500 hover:scale-105"
                />
              </div>
              <div className="flex h-full flex-1 flex-col gap-4 p-6">
                <h3 className="text-lg font-semibold text-ink">{blog.title}</h3>
                <p className="text-sm leading-relaxed text-ink-muted">
                  {blog.excerpt}
                </p>
                <a
                  className="inline-flex items-center text-sm font-semibold text-primary transition hover:text-primary/80"
                  href="/blogs"
                >
                  Read More →
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-primary to-primary-soft text-white relative z-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-4 py-12 text-center lg:flex-row lg:px-6 lg:text-left">
          <div
            data-aos="fade-right"
            data-aos-duration="1000"
            className="max-w-xl"
          >
            <h2 className="text-2xl font-semibold sm:text-3xl">
              Ready to start your language journey?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80 sm:text-base">
              Share your goals and schedule, and our academic counsellors will
              suggest the perfect batch and exam plan for you.
            </p>
          </div>
          <div
            data-aos="fade-left"
            data-aos-delay="200"
            data-aos-duration="1000"
            className="flex flex-wrap items-center justify-center gap-3"
          >
            <a
              href="/contact"
              className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-primary shadow-[0_18px_30px_rgba(15,23,42,0.35)] transition hover:-translate-y-1 hover:bg-white"
            >
              Book a Free Demo
            </a>
            <a
              href="/learn-language"
              className="inline-flex items-center justify-center rounded-full border border-white/70 px-7 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:-translate-y-1 hover:bg-white/10"
            >
              View All Courses
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
